/**
 * Account removal (2026-10-29), as Discord: Disable account and Delete
 * account (Settings > Account > Account removal). The server ends every
 * session at once; this device signs out and the sign-in screen says what
 * happened and how to come back (signing in again lifts either).
 */
import {
  ACCOUNT_DELETION_HOLD_DAYS,
  ApiErrorCode,
  SocialRoutes,
  type ApiError,
  type SelfHold,
} from '@neosocial/protocol';
import { tr } from '../i18n';
import { api } from '../lib/api';
import { signOut, useSession } from '../store/session';
import { toast } from '../ui/overlay';

export type RemovalKind = 'delete' | 'disable';

/** Runs Disable or Delete account; on success this device is signed out with the reason. */
export async function removeAccount(
  kind: RemovalKind,
  password: string,
  code?: string,
): Promise<void> {
  await api.client.post<void>(
    kind === 'delete' ? SocialRoutes.meDelete() : SocialRoutes.meDisable(),
    {
      password,
      ...(code ? { code } : {}),
    },
  );
  const until =
    kind === 'delete'
      ? new Date(Date.now() + ACCOUNT_DELETION_HOLD_DAYS * 86_400_000).toISOString()
      : undefined;
  // the session is over (the gateway may say so first): sign out here, and say why
  await signOut(true);
  useSession.getState().set({
    endedReason: {
      code: ApiErrorCode.ACCOUNT_DISABLED,
      message: '',
      nx_self_hold: kind === 'delete' ? 'deletion' : 'disabled',
      ...(until ? { nx_until: until } : {}),
    },
  });
}

/** The sign-in screen's line for a refusal that is the account's own choice, or null. */
export function selfHoldText(
  body: Pick<ApiError, 'nx_self_hold' | 'nx_until'> | null,
): string | null {
  const hold = body?.nx_self_hold;
  if (!hold) return null;
  const { t, fmt } = tr();
  if (hold === 'deletion')
    return t('nxs.ended.deletion', { date: body?.nx_until ? fmt.date(body.nx_until) : '' });
  return t('nxs.ended.disabled');
}

/** A sign-in lifted the account's own hold: welcome them back. */
export function noteRestored(kind: SelfHold | undefined): void {
  if (!kind) return;
  const { t } = tr();
  toast(t(kind === 'deletion' ? 'nxs.restored.deletion' : 'nxs.restored.disabled'), {
    icon: 'check',
    tone: 'ok',
    duration: 9000,
  });
}
